/**
 * Admin Promo Codes — create/list/toggle/delete discount codes used at /billing checkout.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { api } from "@/App";
import { toast } from "sonner";
import { Plus, Trash2, Power } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

const AdminPromoCodesPage = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [type, setType] = useState("percent");
  const [value, setValue] = useState(20);
  const [maxUses, setMaxUses] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setCodes((await api.get("/admin/promo-codes")).promo_codes); }
    catch { toast.error("Could not load"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setSaving(true);
    try {
      await api.post("/admin/promo-codes", {
        code: code.trim().toUpperCase(),
        discount_type: type,
        discount_value: Number(value),
        max_uses: maxUses ? Number(maxUses) : null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      toast.success("Promo created");
      setCode(""); setValue(20); setMaxUses(""); setExpiresAt("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const toggle = async (p) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/promo-codes/${p.id}`, { active: !p.active }, { withCredentials: true });
      toast.success(p.active ? "Deactivated" : "Activated");
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete promo ${p.code}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/promo-codes/${p.id}`, { withCredentials: true });
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-promos-page">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Promo codes</h2>
        <p className="text-sm text-muted-foreground">Discount codes students can apply at checkout. Use for launch campaigns, partnerships, or refunds.</p>
      </div>

      <form onSubmit={create} className="glass border border-border rounded-2xl p-5 mb-6 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
        <div className="md:col-span-2">
          <label className="text-xs text-muted-foreground">Code</label>
          <input data-testid="promo-code-input" required placeholder="LAUNCH20" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-sm" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Type</label>
          <select data-testid="promo-type" value={type} onChange={(e) => setType(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-sm">
            <option value="percent">% off</option>
            <option value="fixed">$ off</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Value</label>
          <input data-testid="promo-value" required type="number" min={1} value={value} onChange={(e) => setValue(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-sm" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Max uses (optional)</label>
          <input data-testid="promo-max-uses" type="number" min={1} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-sm" />
        </div>
        <button data-testid="promo-create-btn" type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-1">
          <Plus className="w-4 h-4" /> Create
        </button>
      </form>

      <div className="border border-border rounded-2xl overflow-hidden bg-card">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-left text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">Code</th>
              <th className="px-4 py-3 font-medium">Discount</th>
              <th className="px-4 py-3 font-medium">Uses</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && codes.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No promo codes yet.</td></tr>}
            {codes.map((p) => (
              <tr key={p.id} className="border-t border-border">
                <td className="px-4 py-3 font-mono">{p.code}</td>
                <td className="px-4 py-3">{p.discount_type === "percent" ? `${p.discount_value}% off` : `$${p.discount_value} off`}</td>
                <td className="px-4 py-3 text-muted-foreground">{p.uses}{p.max_uses ? `/${p.max_uses}` : ""}</td>
                <td className="px-4 py-3">{p.active ? <span className="text-success">●</span> : <span className="text-muted-foreground">○</span>}</td>
                <td className="px-4 py-3 text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => toggle(p)} title={p.active ? "Deactivate" : "Activate"} className="p-1.5 rounded hover:bg-secondary"><Power className="w-4 h-4 text-muted-foreground" /></button>
                    <button onClick={() => remove(p)} className="p-1.5 rounded hover:bg-destructive/15"><Trash2 className="w-4 h-4 text-destructive" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPromoCodesPage;
